import type {
  NotificationVariable,
  WorkflowDefinitionActionRequest,
  WorkflowDefinitionActionResponse,
  WorkflowDefinitionPhaseRequest,
  WorkflowDefinitionPhaseResponse,
  WorkflowDefinitionRequest,
  WorkflowDefinitionResponse,
  WorkflowDefinitionTriggerRequest,
  WorkflowDefinitionTriggerResponse} from 'src/api/ProactApiClient';
import {
  ItemAssignmentStateEnum,
  ModuleEnum,
  NotificationAttributeEnum,
  NotificationVariableTypeEnum,
  WorkflowActionStateEnum,
  WorkflowDefinitionActionTypeEnum,
  WorkflowDefinitionTriggerTypeEnum,
  WorkflowStateEnum,
  WorkflowTrainingEventTypeEnum,
} from 'src/api/ProactApiClient';
import type {PillProps} from 'src/design/atoms/Pill';
import type {IClientKeyed} from './clientIdentity';

export const WorkflowPillConfig = (status: boolean): PillProps[] => {
  switch (status) {
    case true:
      return [{text: 'Active', color: 'blue'}];
    case false:
      return [{text: 'Inactive', color: 'yellow'}];
    default:
      break;
  }
  return [];
};

export const WorkflowHistoryPillConfig = (
  state: WorkflowStateEnum
): PillProps[] => {
  switch (state) {
    case WorkflowStateEnum.Active:
      return [{text: 'Active', color: 'yellow'}];
    case WorkflowStateEnum.Completed:
      return [{text: 'Completed', color: 'green'}];
    case WorkflowStateEnum.Pending:
      return [{text: 'Pending', color: 'gray'}];
    case WorkflowStateEnum.Waiting:
      return [{text: 'Waiting', color: 'gray'}];
    case WorkflowStateEnum.Cancelled:
      return [{text: 'Cancelled', color: 'red'}];
    case WorkflowStateEnum.Invalid:
      return [{text: 'Invalid', color: 'blue'}];
    default:
      break;
  }
  return [];
};
export const WorkflowTriggerPillConfig = (status: boolean): PillProps[] => {
  switch (status) {
    case true:
      return [{text: 'Activated', color: 'green'}];
    case false:
      return [{text: 'Inactive', color: 'yellow'}];
    default:
      break;
  }
  return [];
};
export const WorkflowDefinitionPillConfig = (status: boolean): PillProps[] => {
  switch (status) {
    case true:
      return [{text: 'Active', color: 'green'}];
    case false:
      return [{text: 'Disabled', color: 'yellow'}];
    default:
      break;
  }
  return [];
};

export const WorkflowPhasePillConfig = (
  state: WorkflowStateEnum
): PillProps[] => {
  switch (state) {
    case WorkflowStateEnum.Active:
      return [{text: 'Active', color: 'yellow'}];
    case WorkflowStateEnum.Completed:
      return [{text: 'Completed', color: 'green'}];
    case WorkflowStateEnum.Pending:
      return [{text: 'Pending', color: 'gray'}];
    case WorkflowStateEnum.Waiting:
      return [{text: 'Waiting', color: 'gray'}];
    case WorkflowStateEnum.Cancelled:
      return [{text: 'Cancelled', color: 'red'}];
    case WorkflowStateEnum.Invalid:
      return [{text: 'Invalid', color: 'blue'}];
    default:
      break;
  }
  return [];
};
export const WorkflowActionPillConfig = (
  state: WorkflowActionStateEnum
): PillProps[] => {
  switch (state) {
    case WorkflowActionStateEnum.Completed:
      return [{text: 'Completed', color: 'green'}];
    case WorkflowActionStateEnum.Cancelled:
      return [{text: 'Cancelled', color: 'yellow'}];
    case WorkflowActionStateEnum.Skipped:
      return [{text: 'Skipped', color: 'blue'}];
    case WorkflowActionStateEnum.Waiting:
      return [{text: 'Waiting', color: 'blue'}];
    case WorkflowActionStateEnum.Pending:
      return [{text: 'Pending', color: 'blue'}];
    case WorkflowActionStateEnum.Invalid:
      return [{text: 'Invalid', color: 'red'}];
    default:
      break;
  }
  return [];
};

export const GetWorkflowTriggerLabel = (
  trigger: WorkflowDefinitionTriggerResponse
): string => {
  switch (trigger.type) {
    case WorkflowDefinitionTriggerTypeEnum.FormFieldValueSubmission:
      return 'When Form Field has value';
    case WorkflowDefinitionTriggerTypeEnum.FormSubmission:
      return 'When Form is submitted';
    case WorkflowDefinitionTriggerTypeEnum.TaskCompletion:
      return 'When Task is completed';
    case WorkflowDefinitionTriggerTypeEnum.Manual:
      return 'When manually triggered';
    case WorkflowDefinitionTriggerTypeEnum.Schedule:
      return 'On schedule';
    case WorkflowDefinitionTriggerTypeEnum.Training:
      return (
        'When Training creates ' +
        GetWorkflowTrainingEventLabel(trigger.triggerTrainingEventType) +
        ' event'
      );
    case WorkflowDefinitionTriggerTypeEnum.ItemTracker:
      return (
        'When Item creates ' +
        GetWorkflowItemTrackerEventLabel(trigger.triggerItemAssignmentState) +
        ' event'
      );
    case WorkflowDefinitionTriggerTypeEnum.Invalid:
    default:
      return 'Invalid Trigger';
  }
};

export const GetWorkflowTrainingEventLabel = (
  type?: WorkflowTrainingEventTypeEnum
): string => {
  if (!type) return 'Invalid';
  return WorkflowTrainingEventTypeEnum[type].toString() ?? 'Invalid';
};

export const GetWorkflowItemTrackerEventLabel = (
  state?: ItemAssignmentStateEnum
): string => {
  if (!state) return 'Invalid';

  return ItemAssignmentStateEnum[state]?.toString().splitUppercase() ?? '';
};

export const GetWorkflowActionLabel = (
  type?: WorkflowDefinitionActionTypeEnum
): string => {
  switch (type) {
    case WorkflowDefinitionActionTypeEnum.AssignTask:
      return 'Assign Task';
    case WorkflowDefinitionActionTypeEnum.CreateTask:
      return 'Create Task';
    case WorkflowDefinitionActionTypeEnum.Email:
      return 'Send Email';
    case WorkflowDefinitionActionTypeEnum.ExtendBadgeExpiration:
      return 'Extend Badge Expiration';
    case WorkflowDefinitionActionTypeEnum.Invalid:
    default:
      return 'Invalid Action';
  }
};

export const MapWorkflowDefinitionResponseToWorkflowDefinitionRequest = (
  data: WorkflowDefinitionResponse
): WorkflowDefinitionRequest => {
  return {
    id: data.id,
    version: data.version,
    name: data.name,
    active: data.active,
    projectId: data.projectId,
    metadata: {
      singleTriggerWorkflow: data.triggers.length !== 1,
    },
    targetPhases: data.phases.map(phase => {
      const {clientKey: _phaseKey, ...phaseRest} =
        phase as WorkflowDefinitionPhaseResponse & IClientKeyed;
      return {
        ...phaseRest,
        targetActions: phase.actions.map(action => {
          const {clientKey: _actionKey, ...actionRest} =
            action as WorkflowDefinitionActionResponse & IClientKeyed;
          return {...actionRest} as WorkflowDefinitionActionRequest;
        }),
      } as WorkflowDefinitionPhaseRequest;
    }),
    targetTriggers: data.triggers.map(trigger => {
      const {clientKey: _triggerKey, ...triggerRest} =
        trigger as WorkflowDefinitionTriggerResponse & IClientKeyed;
      return {...triggerRest} as WorkflowDefinitionTriggerRequest;
    }),
  } as WorkflowDefinitionRequest;
};

export const WorkflowDocumentTitle = 'Workflow';

export const WorkflowTargetAttributes = [
  {
    value: String(NotificationAttributeEnum.Assignee),
    label: 'Triggerer of Workflow',
  },
  {
    value: String(NotificationAttributeEnum.Supervisor),
    label: 'Supervisor of Triggerer',
  },
  {
    value: String(NotificationAttributeEnum.SuperSupervisor),
    label: 'SuperSupervisor of Triggerer',
  },
];

export const notificationVariables = {
  usersName: {
    type: NotificationVariableTypeEnum.System,
    variable: 'USER_NAME',
    description: 'The name of the user who triggered the workflow',
    example: 'John Smith',
  } as NotificationVariable,
  usersSupervisorName: {
    type: NotificationVariableTypeEnum.System,
    variable: 'USER_SUPERVISOR_NAME',
    description: "The supervisor's name of the user who triggered the workflow",
    example: 'Ruth Griffin',
  } as NotificationVariable,
  usersEmail: {
    type: NotificationVariableTypeEnum.System,
    variable: 'USER_EMAIL',
    description: 'The email of the user who triggered the workflow',
    example: 'john.smith@example.com',
  } as NotificationVariable,
  usersSupervisorEmail: {
    type: NotificationVariableTypeEnum.System,
    variable: 'USER_SUPERVISOR_EMAIL',
    description:
      "The supervisor's email of the user who triggered the workflow",
    example: 'ruth.griffin@example.com',
  } as NotificationVariable,
};

export const getCommonVariablesForWorkflow = (
  action: WorkflowDefinitionActionResponse,
  isScheduled: boolean
): NotificationVariable[] => {
  let variables: NotificationVariable[] = [];

  // Currently, no variables are available for scheduled workflows.
  if (isScheduled) {
    variables = [];
  } else {
    variables = [
      notificationVariables.usersName,
      notificationVariables.usersEmail,
      notificationVariables.usersSupervisorName,
      notificationVariables.usersSupervisorEmail,
    ];
  }

  return variables;
};

export function getWorkflowStateColor(state: string): string {
  switch (state.toLowerCase()) {
    case 'active':
      return 'yellow';
    case 'completed':
      return 'green';
    case 'pending':
    case 'waiting':
      return 'gray';
    case 'cancelled':
      return 'red';
    case 'invalid':
      return 'blue';
    default:
      return 'gray';
  }
}

export const MapWorkflowDefinitionTriggerTypeToModule = (
  type?: WorkflowDefinitionTriggerTypeEnum
): ModuleEnum => {
  if (type === WorkflowDefinitionTriggerTypeEnum.FormFieldValueSubmission)
    return ModuleEnum.Forms;
  if (type === WorkflowDefinitionTriggerTypeEnum.FormSubmission)
    return ModuleEnum.Forms;
  if (type === WorkflowDefinitionTriggerTypeEnum.ItemTracker)
    return ModuleEnum.ItemTrackerAssignments;
  if (type === WorkflowDefinitionTriggerTypeEnum.TaskCompletion)
    return ModuleEnum.Invalid;
  if (type === WorkflowDefinitionTriggerTypeEnum.Training)
    return ModuleEnum.Training;
  return ModuleEnum.Invalid;
};
